// src/components/opportunity/GenerateProposalButton.tsx
//
// Workspace-header actions for document packages (proposal or contract):
// Preview + Save. Unsaved Pricing edits are auto-saved before render so
// the PDF matches what the sales user just typed.

import { useState, useEffect, useMemo } from "react";
import type {
  Opportunity,
  OpportunityPricingSnapshot,
  OpportunityRevision,
} from "@/types/opportunity";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { useAuth } from "@/contexts/AuthContext";
import { fetchRepContactLocation } from "@/lib/opportunity/fetchRepContactLocation";
import type { RepContactLocation } from "@/lib/opportunity/fetchRepContactLocation";
import {
  generateAndUploadOpportunityDocumentPackage,
  renderOpportunityDocumentPackageToBlob,
} from "@/components/opportunity/pdf/generateProposalPdf";
import { OpportunityProposalPreviewModal } from "@/components/opportunity/OpportunityProposalPreviewModal";
import { ProposalAlternatePicker } from "@/components/opportunity/ProposalAlternatePicker";
import { defaultAlternateForArchetype } from "@/lib/opportunity/proposalAlternateTypes";
import { resolveManagerPresetFormatKey } from "@/lib/opportunity/formatRegistry";
import {
  OPPORTUNITY_DOCUMENT_ARCHETYPE_LABELS,
  OPPORTUNITY_DOCUMENT_ARCHETYPES,
  type OpportunityDocumentArchetype,
} from "@/lib/opportunity/documentArchetypes";

export type EnsurePricingSavedResult =
  | {
      ok: true;
      opportunity: Opportunity;
      activeSnapshot: OpportunityPricingSnapshot;
      revisions: OpportunityRevision[];
    }
  | { ok: false; error: string };

export type GenerateProposalButtonProps = {
  opportunity: Opportunity;
  activeSnapshot: OpportunityPricingSnapshot | null;
  revisions: OpportunityRevision[];
  onGenerated: (outputId: string) => void;
  /** Persist dirty Pricing edits + reload before rendering the PDF. */
  ensurePricingSaved: () => Promise<EnsurePricingSavedResult>;
};

export function GenerateProposalButton(props: GenerateProposalButtonProps) {
  const { opportunity, activeSnapshot, revisions, onGenerated, ensurePricingSaved } = props;
  const org = useOrgSettings();
  const auth = useAuth();
  const [activeLocation, setActiveLocation] = useState<RepContactLocation | null>(null);
  const [busy, setBusy] = useState<"none" | "previewing" | "saving">("none");
  const [error, setError] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewAlternateKey, setPreviewAlternateKey] = useState("");
  const [previewAlternateLabel, setPreviewAlternateLabel] = useState("");
  const [archetype, setArchetype] = useState<OpportunityDocumentArchetype>("proposal");
  const [alternateKey, setAlternateKey] = useState("");
  const [alternateLabel, setAlternateLabel] = useState("");

  useEffect(() => {
    const locId = auth.profile?.active_location_id;
    if (!locId) {
      setActiveLocation(null);
      return;
    }
    let cancelled = false;
    void fetchRepContactLocation(locId).then((loc) => {
      if (cancelled) return;
      setActiveLocation(loc);
    });
    return () => {
      cancelled = true;
    };
  }, [auth.profile?.active_location_id]);

  useEffect(() => {
    // Default: Proposal + Primary. Alternates are explicit-pick only.
    const formatKey = resolveManagerPresetFormatKey(opportunity.product_type);
    const standard = defaultAlternateForArchetype({
      formatKey,
      productType: opportunity.product_type,
      archetype,
    });
    setAlternateKey(standard.alternate_key);
    setAlternateLabel(standard.label);
    setPreviewBlob(null);
  }, [opportunity.product_type, archetype]);

  const activeRevision =
    revisions.find((r) => r.id === opportunity.active_revision_id) ?? null;
  const disabled = !activeSnapshot || busy !== "none";
  const familyLabel = OPPORTUNITY_DOCUMENT_ARCHETYPE_LABELS[archetype];

  const buildRenderArgs = useMemo(() => {
    return (ctx: {
      opportunity: Opportunity;
      activeSnapshot: OpportunityPricingSnapshot;
      revisions: OpportunityRevision[];
    }) => {
      const revision =
        ctx.revisions.find((r) => r.id === ctx.opportunity.active_revision_id) ?? null;
      return {
        opportunity: ctx.opportunity,
        snapshot: ctx.activeSnapshot,
        revision,
        brandName: org.brandName || org.organizationName,
        organizationName: org.organizationName,
        tagline: org.tagline,
        logoUrl: org.logoUrl,
        proposalContactRow: org.proposalContactRow,
        profile: auth.profile,
        activeLocation,
        alternateKey,
        archetype,
      };
    };
  }, [
    activeLocation,
    alternateKey,
    archetype,
    auth.profile,
    org.brandName,
    org.organizationName,
    org.logoUrl,
    org.proposalContactRow,
    org.tagline,
  ]);

  async function openPreview() {
    setBusy("previewing");
    setError(null);
    try {
      const ensured = await ensurePricingSaved();
      if (!ensured.ok) {
        setError(ensured.error);
        return;
      }
      const r = await renderOpportunityDocumentPackageToBlob(buildRenderArgs(ensured));
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setPreviewAlternateKey(r.alternateKey);
      setPreviewAlternateLabel(r.alternateLabel);
      setPreviewBlob(r.blob);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to render preview");
    } finally {
      setBusy("none");
    }
  }

  async function saveWithoutPreview() {
    setBusy("saving");
    setError(null);
    try {
      const ensured = await ensurePricingSaved();
      if (!ensured.ok) {
        setError(ensured.error);
        return;
      }
      const r = await generateAndUploadOpportunityDocumentPackage(buildRenderArgs(ensured));
      if (!r.ok) {
        setError(r.error);
        return;
      }
      onGenerated(r.outputId);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to save ${familyLabel.toLowerCase()}`);
    } finally {
      setBusy("none");
    }
  }

  const disabledTitle = !activeSnapshot
    ? `Save a pricing revision before generating a ${familyLabel.toLowerCase()}.`
    : "Renders a PDF from the latest pricing. Unsaved Pricing edits are saved first.";

  return (
    <>
      <div className="flex flex-col items-end gap-2">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-zinc-300">
            Document type
            <select
              value={archetype}
              disabled={disabled}
              onChange={(e) => setArchetype(e.target.value as OpportunityDocumentArchetype)}
              className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
            >
              {OPPORTUNITY_DOCUMENT_ARCHETYPES.map((key) => (
                <option key={key} value={key}>
                  {OPPORTUNITY_DOCUMENT_ARCHETYPE_LABELS[key]}
                </option>
              ))}
            </select>
          </label>
          <ProposalAlternatePicker
            productType={opportunity.product_type}
            archetype={archetype}
            value={alternateKey}
            disabled={disabled}
            onChange={(key, alt) => {
              setAlternateKey(key);
              setAlternateLabel(alt.label);
              setPreviewBlob(null);
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void openPreview()}
            disabled={disabled}
            title={disabledTitle}
            className="px-3 py-1.5 rounded-lg border border-indigo-200 bg-white text-indigo-700 text-sm hover:bg-indigo-50 dark:border-indigo-800 dark:bg-zinc-900 dark:text-indigo-200 dark:hover:bg-indigo-950/40 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy === "previewing" ? "Rendering…" : "Preview"}
          </button>
          <button
            type="button"
            onClick={() => void saveWithoutPreview()}
            disabled={disabled}
            title={disabledTitle}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy === "saving" ? "Saving…" : `Save ${familyLabel.toLowerCase()}`}
          </button>
        </div>
        {alternateLabel ? (
          <span className="text-[10px] text-gray-500 dark:text-zinc-400">
            Package: {alternateLabel}
          </span>
        ) : null}
        {error ? (
          <span className="text-xs text-red-700 dark:text-red-300 max-w-xs text-right">
            {error}
          </span>
        ) : null}
      </div>

      <OpportunityProposalPreviewModal
        open={previewBlob !== null}
        blob={previewBlob}
        opportunity={opportunity}
        revision={activeRevision}
        archetype={archetype}
        alternateKey={previewAlternateKey}
        alternateLabel={previewAlternateLabel}
        onSaved={(outputId) => {
          setPreviewBlob(null);
          onGenerated(outputId);
        }}
        onClose={() => setPreviewBlob(null)}
      />
    </>
  );
}
